// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

import { readCapabilities } from "../features/access/api/capabilitiesRead";
import { useAuthSession } from "../hooks/useAuthSession";

export interface TeamsFeatureState {
  teams: boolean;
  settled: boolean;
}

const PENDING: TeamsFeatureState = { teams: false, settled: false };
const FAILED_CLOSED: TeamsFeatureState = { teams: false, settled: true };

const RETRY_DELAYS_MS: readonly number[] = [2000, 5000, 15000];

const FeatureFlagsContext = createContext<TeamsFeatureState>(PENDING);

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { getAccessToken } = useAuthSession();
  const [teamsFeature, setTeamsFeature] = useState<TeamsFeatureState>(PENDING);

  useEffect(() => {
    let cancelled = false;
    let retries = 0;
    let retryTimer: number | undefined;

    const load = (): void => {
      void getAccessToken()
        .catch(() => undefined)
        .then((token) => readCapabilities(token !== undefined ? { token } : {}))
        .then((model) => {
          if (!cancelled) {
            setTeamsFeature({ teams: model.features.teams, settled: true });
          }
        })
        .catch(() => {
          if (cancelled) {
            return;
          }
          setTeamsFeature(FAILED_CLOSED);
          const delay = RETRY_DELAYS_MS[retries];
          if (delay === undefined) {
            return;
          }
          retries += 1;
          retryTimer = window.setTimeout(load, delay);
        });
    };

    load();
    return () => {
      cancelled = true;
      if (retryTimer !== undefined) {
        window.clearTimeout(retryTimer);
      }
    };
  }, [getAccessToken]);

  return (
    <FeatureFlagsContext.Provider value={teamsFeature}>{children}</FeatureFlagsContext.Provider>
  );
}

export function useTeamsEnabled(): TeamsFeatureState {
  return useContext(FeatureFlagsContext);
}
